import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts'
import { api } from '../lib/api'
import { Card } from './ui/Card'

interface DashboardMetrics {
  customers: {
    total: number
    vip: number
    recurrent: number
    churned: number
    churnRate: number
  }
  revenue30: { amount: string; orders: number }
  revenue90: { amount: string; orders: number }
  segments: Array<{ segment: string; count: number; totalSpent: string; avgLtv: string }>
}

interface AlertCustomer {
  id: number
  firstName: string | null
  lastName: string | null
  email: string | null
  ordersCount: number
  totalSpent: string
  daysSinceLastOrder: number | null
  lastOrderAt: string | null
}

interface AlertsResp {
  vipDormant: AlertCustomer[]
  recurrentDormant: AlertCustomer[]
}

interface ChartsResp {
  monthlyRevenue: Array<{ month: string; revenue: string; orders: number }>
  topProducts: Array<{ sku: string | null; title: string; qty: number; revenue: string }>
  topCities: Array<{ city: string; orders: number; revenue: string }>
}

const SEGMENT_LABELS: Record<string, string> = {
  VIP: 'VIP',
  recurrent: 'Recurenți',
  active: 'Activi',
  at_risk: 'At-risk',
  reactivation: 'Reactivare',
  churned: 'Churned',
  one_time: 'O singură comandă',
  unknown: 'Nesegmentat',
}

export function ShopifyDashboardWidget() {
  const metricsQuery = useQuery<DashboardMetrics>({
    queryKey: ['shopify-dashboard-metrics'],
    queryFn: async () => (await api.get<DashboardMetrics>('/shopify/dashboard-metrics')).data,
    refetchInterval: 5 * 60_000,
    retry: 1,
  })

  const alertsQuery = useQuery<AlertsResp>({
    queryKey: ['shopify-alerts'],
    queryFn: async () => (await api.get<AlertsResp>('/shopify/alerts')).data,
    refetchInterval: 5 * 60_000,
    retry: 1,
  })

  const chartsQuery = useQuery<ChartsResp>({
    queryKey: ['shopify-dashboard-charts'],
    queryFn: async () => (await api.get<ChartsResp>('/shopify/dashboard-charts')).data,
    refetchInterval: 10 * 60_000,
    retry: 1,
  })

  if (metricsQuery.isError || alertsQuery.isError) {
    return null // 403/permisiuni → ascunde widget-ul
  }

  const m = metricsQuery.data
  const a = alertsQuery.data

  return (
    <Card
      title="Shopify - Boost4Life.ro"
      description="Sincronizare automată ordere + segmente clienți"
    >
      {!m && <p className="text-sm text-b4l-text-muted">Se încarcă...</p>}
      {m && (
        <>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <Stat label="Clienți totali" value={m.customers.total} />
            <Stat
              label="VIP"
              value={m.customers.vip}
              accent="purple"
              link="/marketing/shopify/clienti"
            />
            <Stat
              label="Recurenți"
              value={m.customers.recurrent}
              accent="green"
              link="/marketing/shopify/clienti"
            />
            <Stat
              label="Churn rate"
              value={`${m.customers.churnRate}%`}
              accent={m.customers.churnRate > 50 ? 'red' : 'amber'}
              hint={`${m.customers.churned} churned`}
            />
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="rounded border border-b4l-border bg-b4l-bg-soft p-3">
              <div className="text-[10px] uppercase tracking-wide text-b4l-text-muted">
                Revenue 30 zile
              </div>
              <div className="mt-1 text-xl font-semibold">
                {Number(m.revenue30.amount).toLocaleString('ro-RO', {
                  maximumFractionDigits: 0,
                })}{' '}
                RON
              </div>
              <div className="text-[11px] text-b4l-text-dim">
                {m.revenue30.orders} comenzi vândute · fără transport
              </div>
            </div>
            <div className="rounded border border-b4l-border bg-b4l-bg-soft p-3">
              <div className="text-[10px] uppercase tracking-wide text-b4l-text-muted">
                Revenue 90 zile
              </div>
              <div className="mt-1 text-xl font-semibold">
                {Number(m.revenue90.amount).toLocaleString('ro-RO', {
                  maximumFractionDigits: 0,
                })}{' '}
                RON
              </div>
              <div className="text-[11px] text-b4l-text-dim">
                {m.revenue90.orders} comenzi vândute · fără transport
              </div>
            </div>
          </div>
        </>
      )}

      {a && (a.vipDormant.length > 0 || a.recurrentDormant.length > 0) && (
        <div className="mt-4 border-t border-b4l-border pt-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-b4l-amber-dark">
              ⚠ Alerte clienți
            </div>
            <Link
              to="/marketing/shopify/clienti"
              className="text-[11px] text-b4l-green-dark hover:underline"
            >
              Vezi toți clienții
            </Link>
          </div>
          {a.vipDormant.length > 0 && (
            <AlertBlock
              title={`VIP fără comandă > 60z (${a.vipDormant.length})`}
              tone="amber"
              customers={a.vipDormant.slice(0, 5)}
            />
          )}
          {a.recurrentDormant.length > 0 && (
            <AlertBlock
              title={`Recurenți fără comandă > 90z (${a.recurrentDormant.length})`}
              tone="amber"
              customers={a.recurrentDormant.slice(0, 5)}
            />
          )}
        </div>
      )}

      {chartsQuery.data && chartsQuery.data.monthlyRevenue.length > 0 && (
        <div className="mt-4 border-t border-b4l-border pt-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-b4l-text-dim">
            Evoluție revenue (ultimele 12 luni)
          </div>
          <div className="h-48 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartsQuery.data.monthlyRevenue.map((r) => ({
                  month: r.month.slice(2),
                  revenue: Math.round(Number(r.revenue)),
                  orders: r.orders,
                }))}
                margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`)}
                />
                <Tooltip
                  formatter={(v: unknown, name: unknown) => {
                    const num = Number(v)
                    return name === 'revenue'
                      ? [`${num.toLocaleString('ro-RO')} RON`, 'Revenue']
                      : [String(v), 'Comenzi']
                  }}
                  labelFormatter={(label: unknown) => `Luna 20${String(label)}`}
                />
                <Bar dataKey="revenue" fill="#378add" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {chartsQuery.data && (
        <div className="mt-4 grid grid-cols-1 gap-4 border-t border-b4l-border pt-3 md:grid-cols-2">
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-b4l-text-dim">
              Top 5 produse (90 zile)
            </div>
            <TopList
              items={chartsQuery.data.topProducts.map((p) => ({
                label: p.sku ? `${p.sku} · ${p.title}` : p.title,
                value: Number(p.revenue),
                hint: `${p.qty} buc`,
              }))}
              unit="RON"
            />
          </div>
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-b4l-text-dim">
              Top 5 orașe (90 zile)
            </div>
            <TopList
              items={chartsQuery.data.topCities.map((c) => ({
                label: c.city,
                value: Number(c.revenue),
                hint: `${c.orders} ordere`,
              }))}
              unit="RON"
            />
          </div>
        </div>
      )}

      {m && m.segments.length > 0 && (
        <div className="mt-4 border-t border-b4l-border pt-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-b4l-text-dim">
            Distribuție segmente
          </div>
          <div className="flex flex-wrap gap-2">
            {m.segments.map((s) => (
              <Link
                key={s.segment}
                to={`/marketing/shopify/clienti`}
                className="rounded border border-b4l-border bg-white px-2 py-1 text-xs hover:border-b4l-green"
              >
                <span className="font-medium">{SEGMENT_LABELS[s.segment] ?? s.segment}</span>
                <span className="ml-1 text-b4l-text-muted">
                  · {s.count} · LTV ~{Number(s.avgLtv).toFixed(0)} RON
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </Card>
  )
}

function TopList({
  items,
  unit,
}: {
  items: Array<{ label: string; value: number; hint?: string }>
  unit: string
}) {
  if (items.length === 0) {
    return <div className="text-xs text-b4l-text-muted">Fără date</div>
  }
  const max = Math.max(...items.map((i) => i.value), 1)
  return (
    <ul className="space-y-1.5">
      {items.map((item, i) => {
        const pct = (item.value / max) * 100
        return (
          <li key={i}>
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="truncate font-medium" title={item.label}>
                {item.label}
              </span>
              <span className="whitespace-nowrap text-b4l-text-muted">
                {item.value.toLocaleString('ro-RO', { maximumFractionDigits: 0 })} {unit}
                {item.hint && <span className="ml-1 text-b4l-text-dim">· {item.hint}</span>}
              </span>
            </div>
            <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded bg-b4l-bg-soft">
              <div className="h-full bg-b4l-blue" style={{ width: `${pct}%` }} />
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function Stat({
  label,
  value,
  accent,
  link,
  hint,
}: {
  label: string
  value: string | number
  accent?: 'purple' | 'green' | 'amber' | 'red'
  link?: string
  hint?: string
}) {
  const cls =
    accent === 'purple'
      ? 'border-l-b4l-purple'
      : accent === 'green'
        ? 'border-l-b4l-green'
        : accent === 'amber'
          ? 'border-l-b4l-amber'
          : accent === 'red'
            ? 'border-l-b4l-red'
            : 'border-l-b4l-border'
  const inner = (
    <div
      className={`rounded border-l-4 border border-b4l-border bg-white p-2 ${cls} ${
        link ? 'transition hover:shadow-sm' : ''
      }`}
    >
      <div className="text-[10px] uppercase tracking-wide text-b4l-text-muted">{label}</div>
      <div className="mt-0.5 text-xl font-semibold">{value}</div>
      {hint && <div className="text-[10px] text-b4l-text-dim">{hint}</div>}
    </div>
  )
  return link ? <Link to={link}>{inner}</Link> : inner
}

function AlertBlock({
  title,
  tone,
  customers,
}: {
  title: string
  tone: 'amber' | 'red'
  customers: AlertCustomer[]
}) {
  const cls = tone === 'red' ? 'bg-b4l-red-light/30' : 'bg-b4l-amber-light/30'
  return (
    <div className={`mb-2 rounded border border-b4l-border p-2 ${cls}`}>
      <div className="mb-1 text-xs font-medium">{title}</div>
      <ul className="space-y-0.5 text-xs">
        {customers.map((c) => {
          const name =
            c.firstName || c.lastName
              ? `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim()
              : (c.email ?? '(fără nume)')
          return (
            <li key={c.id} className="flex justify-between gap-2">
              <Link
                to={`/marketing/shopify/clienti`}
                className="truncate hover:text-b4l-green-dark"
              >
                {name}
              </Link>
              <span className="whitespace-nowrap text-b4l-text-muted">
                {c.daysSinceLastOrder ?? '-'}z · {Number(c.totalSpent).toFixed(0)} RON
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
